import React, { useState, useEffect } from 'react';
import { Save, X, Trash2, FileText, Package, Box, Truck, Clock, Receipt, FolderOpen, HelpCircle } from 'lucide-react';
import api from '../services/api';
import PrintDocuments from './PrintDocuments';
import BasicInfoTab    from './tabs/BasicInfoTab';
import DocumentsTab    from './tabs/DocumentsTab';
import CargoTab        from './tabs/CargoTab';
import ContainerTab    from './tabs/ContainerTab';
import InvoiceTab      from './tabs/InvoiceTab';
import BillOfEntryTab  from './tabs/Billofentrytab';
import TransportTab    from './tabs/TransportTab';
import AuditLogTab     from './tabs/Auditlogtab';

const PRIMARY      = '#2563EB';
const PRIMARY_DARK = '#1D4ED8';

const JobForm = ({ jobId, onClose, onSaved }) => {
    const [activeTab,        setActiveTab]        = useState('basic');
    const [isLoading,        setIsLoading]        = useState(false);
    const [isSaving,         setIsSaving]         = useState(false);
    const [isGeneratingCode, setIsGeneratingCode] = useState(false);
    const [masterData,       setMasterData]       = useState({});
    const [errors,           setErrors]           = useState({});
    const [showHelp,         setShowHelp]         = useState(false);

    const [formData, setFormData] = useState({
        JobID: null, JobCode: '', JobTypeID: null,
        JobDate: new Date().toISOString().split('T')[0],
        ConsignerID: null, ConsigneeID: null, InvoiceToID: null,
        ShipperID: null, EmployeeID: null, VesselID: null, VoyageNo: '',
        AirlineID: null, Flight: '', MAWB: '', HAWB: '', MBL: '', HBL: '',
        LoadPortID: null, DestinationPortID: null, ReceiptPlaceID: null, DeliveryPlaceID: null,
        BLDate: new Date().toISOString().split('T')[0],
        SailingDate: new Date().toISOString().split('T')[0],
        ArrivalDate: new Date().toISOString().split('T')[0],
        CollectionDate: new Date().toISOString().split('T')[0],
        DeliveryDate: new Date().toISOString().split('T')[0],
        TransporterID: null, CarrierID: null, TruckRegNo: '', DriverDetails: '',
        IPTNo: '', RefNo: '', DeliveryOrderNo: '', DeliveryOrderDate: '', CustRef: '',
        GrossWeight: '', Volume: '', Package: '', BLStatus: '', Freight: '',
        StausInvoiceType: 'CR', CLFValue: '', DepositAmount: '', DepositDate: '',
        ReceiptNo: '', RefundAmount: '', RefundDate: '',
        ShippingInstruction: '', DeliveryInstructions: '', CollectionInstructions: '',
        CountryofOriginID: null, MRN: '', RotationNo: '', CollectionPoint: '',
        DeliveryNote: '', DeliveryPoint: '', Remarks: '', TotalTax: 0,
        InvoiceNo: '', InvoiceDate: '', StatusSub: false, MainJobID: null,
        QuotationID: null, cargoDescriptions: [], containerDetails: [],
        invoiceDetails: [], billOfEntry: [], auditLog: [],
    });

    const getSessionData = () => {
        try { const s=localStorage.getItem('ltms_session'); if(!s) return null; const p=JSON.parse(s); return p?.session||p; }
        catch { return null; }
    };
    const companyName = getSessionData()?.companyname || '';

    useEffect(() => {
        const init = async () => {
            await loadMasterData();
            if (jobId) await loadJobData(jobId);
            else       initializeEmptyTables();
        };
        init();
    }, [jobId]);

    const initializeEmptyTables = () => {
        setFormData(prev => ({
            ...prev,
            cargoDescriptions: [{ CargoDescriptionID:null, Mark:'', Description:'', Weight:0, Volume:0, Packages:0, GrossWeight:0 }],
            containerDetails:  [{ JContainerDetailID:null, ContainerTypeID:null, ContainerNo:'', SealNo:'', Description:'' }],
            invoiceDetails:    [{ InvoiceID:null, RevenueTypeID:null, ProvisionCurrencyID:null, ProvisionExchangeRate:1, ProvisionForeign:0, ProvisionHome:0, ProvisionRate:0, SalesCurrencyID:null, SalesExchangeRate:1, SalesForeign:0, SalesHome:0, SalesRate:0, Cost:0, SupplierID:null, RevenueCode:'', Quantity:0, UnitID:null, TaxID:null, TaxPer:0, TaxAmt:0, TaxAmtF:0, AmtInWords:'' }],
            billOfEntry:       [{ BillOfEntryID:null, BillOfEntry:'', BillofEntryDate:new Date().toISOString().split('T')[0], ShippingAgentID:null }],
            auditLog: [],
        }));
    };

    const loadMasterData = async () => {
        try {
            const res = await api.get('/JobGeneration.php/masters');
            if (res.data.success) setMasterData(res.data.data);
            else throw new Error(res.data.message || 'Failed to load master data');
        } catch (err) { alert('Failed to load dropdown data: ' + (err.response?.data?.message || err.message)); }
    };

    const loadJobData = async (id) => {
        try {
            setIsLoading(true);
            const res = await api.get(`/JobGeneration.php/${id}`);
            if (res.data.success) setFormData(res.data.data);
            else throw new Error(res.data.message || 'Failed to load job');
        } catch (err) { alert('Failed to load job: ' + (err.response?.data?.message || err.message)); }
        finally { setIsLoading(false); }
    };

    const generateJobCode = async (jobTypeId, jobDate, mainJobId=null) => {
        if (!jobTypeId || !jobDate) return;
        if (formData.StatusSub && !mainJobId) return;
        try {
            setIsGeneratingCode(true);
            let url = `/JobGeneration.php/next-code?jobtypeid=${jobTypeId}&jobdate=${jobDate}`;
            if (mainJobId) url += `&mainjobid=${mainJobId}`;
            const res = await api.get(url);
            if (res.data.success) setFormData(prev => ({ ...prev, JobCode: res.data.data.jobCode }));
        } catch (err) { console.error('Error generating job code:', err); }
        finally { setIsGeneratingCode(false); }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => {
            const updated = { ...prev, [field]: value };
            if (field === 'InvoiceToID' && value && !prev.StatusSub) { updated.ConsignerID=value; updated.ConsigneeID=value; }
            return updated;
        });
        if (errors[field]) setErrors(prev => { const n={...prev}; delete n[field]; return n; });
        if (field==='JobTypeID') setFormData(prev => { generateJobCode(value, prev.JobDate, prev.MainJobID); return prev; });
        if (field==='JobDate')   setFormData(prev => { if(prev.JobTypeID) generateJobCode(prev.JobTypeID, value, prev.MainJobID); return prev; });
        if (field==='MainJobID') setFormData(prev => { if(prev.JobTypeID&&prev.JobDate&&value) generateJobCode(prev.JobTypeID, prev.JobDate, value); return prev; });
    };

    const handleChildDataChange = (childTable, data) => setFormData(prev => ({ ...prev, [childTable]: data }));

    const validateForm = () => {
        const newErrors = {};
        if (!formData.JobCode)     newErrors.JobCode     = 'Job code is required';
        if (!formData.JobTypeID)   newErrors.JobTypeID   = 'Job type is required';
        if (!formData.JobDate)     newErrors.JobDate     = 'Job date is required';
        if (!formData.InvoiceToID) newErrors.InvoiceToID = 'Invoice To is required';
        if (formData.StatusSub && !formData.MainJobID) newErrors.MainJobID = 'Main job is required for sub-jobs';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) { alert('Please fill all required fields'); return; }
        let saveData = { ...formData };
        const hasInvoiceRows = formData.invoiceDetails?.some(r => r.RevenueTypeID);
        if (hasInvoiceRows && !formData.InvoiceNo) {
            try {
                const res = await api.get('/JobGeneration.php?action=next-invoice-no');
                if (res.data?.success) {
                    saveData = { ...saveData, InvoiceNo:res.data.data.invoiceNo, InvoiceDate:saveData.InvoiceDate||new Date().toISOString().slice(0,10) };
                    setFormData(saveData);
                }
            } catch (err) { console.warn('Could not auto-generate invoice number:', err.message); }
        }
        try {
            setIsSaving(true);
            const res = jobId ? await api.put('/JobGeneration.php', saveData) : await api.post('/JobGeneration.php', saveData);
            if (res.data.success) {
                alert(jobId ? 'Job updated successfully' : 'Job created successfully');
                if (onSaved) onSaved(res.data.data.jobId);
                if (onClose) onClose();
            } else throw new Error(res.data.message || 'Save failed');
        } catch (err) { alert('Error saving job: ' + (err.response?.data?.message || err.message)); }
        finally { setIsSaving(false); }
    };

    const handleDelete = async () => {
        if (!jobId) return;
        if (!window.confirm('Are you sure you want to delete this job?')) return;
        try {
            const res = await api.delete(`/JobGeneration.php/${jobId}`);
            if (res.data.success) { alert('Job deleted successfully'); if (onClose) onClose(); if (onSaved) onSaved(); }
            else throw new Error(res.data.message || 'Delete failed');
        } catch (err) { alert('Error deleting job: ' + (err.response?.data?.message || err.message)); }
    };

    const tabs = [
        { id:'basic',       label:'Basic Info',   icon:FileText   },
        { id:'cargo',       label:'Cargo',         icon:Package    },
        { id:'container',   label:'Containers',    icon:Box        },
        { id:'invoice',     label:'Invoice',       icon:Receipt    },
        { id:'billofentry', label:'Bill of Entry', icon:FileText   },
        { id:'transport',   label:'Transport',     icon:Truck      },
        { id:'documents',   label:'Documents',     icon:FolderOpen },
        { id:'audit',       label:'Audit Log',     icon:Clock      },
    ];

    const helpSections = [
        { title: 'Basic Info', desc: 'Set Job Type and Job Date to auto-generate the Job Code. Toggle "Sub-Job" to link this job to a Main Job — customer fields will pre-fill from it. Selecting "Invoice To" auto-fills Consigner and Consignee.' },
        { title: 'Cargo', desc: 'Add one row per cargo item with mark, description, weight, volume, and packages. Totals are calculated automatically at the bottom.' },
        { title: 'Containers', desc: 'Add container entries with type, container number, and seal number. At least one row is required.' },
        { title: 'Invoice', desc: 'Add revenue/cost lines by Revenue Type. Tax amounts calculate automatically from Sales value and Tax %. Generate an Invoice No once at least one line has a Revenue Type set.' },
        { title: 'Bill of Entry', desc: 'Add bill of entry numbers with date and shipping agent, if applicable to this job.' },
        { title: 'Transport', desc: 'Set transporter, carrier, truck registration, and driver details. Add collection/delivery points and any special instructions.' },
        { title: 'Documents', desc: 'Upload supporting files (PDF, images, Word, Excel). You must save the job first before documents can be attached.' },
        { title: 'Audit Log', desc: 'Add dated remarks as notes on the job. Existing entries cannot be edited or deleted once saved.' },
    ];

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-gray-500 gap-4">
                <span className="w-10 h-10 border-4 border-gray-200 border-t-[#2563EB] rounded-full block" style={{ animation:'spin 0.8s linear infinite' }} />
                <p className="m-0 text-sm">Loading job data...</p>
                <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-md overflow-hidden max-w-[1400px] mx-auto">
            <style>{`
                @keyframes spin  { to { transform:rotate(360deg); } }
                @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.7} }
            `}</style>

            {/* Header */}
            <div className="flex justify-between items-center px-5 py-5 border-b-2 border-gray-200"
                style={{ background:`linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`, color:'#fff' }}>
                <div className="flex items-center gap-0 flex-wrap">
                    <h2 className="m-0 text-2xl font-semibold">
                        {jobId ? `Edit Job — ${formData.JobCode}` : 'New Job'}
                    </h2>
                    {formData.StatusSub && (
                        <span className="inline-block px-3 py-1 rounded-xl text-xs font-semibold ml-3 uppercase" style={{ background:'#FEF3C7', color:'#B45309' }}>SUB-JOB</span>
                    )}
                    {formData.InvoiceNo && (
                        <span className="inline-block px-3 py-1 rounded-xl text-xs ml-3 text-white" style={{ background:'#16A34A' }}>Invoice: {formData.InvoiceNo}</span>
                    )}
                    {isGeneratingCode && (
                        <span className="inline-block px-3 py-1 rounded-xl text-xs ml-3 text-white" style={{ background:'#F59E0B', animation:'pulse 1.5s ease-in-out infinite' }}>⏳ Generating code…</span>
                    )}
                </div>
                <div className="flex gap-2.5">
                    <button onClick={handleSave} disabled={isSaving || isGeneratingCode}
                        className="flex items-center gap-2 px-4 py-2.5 text-white border-0 rounded-md text-sm font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
                        style={{ backgroundColor: PRIMARY }}
                        onMouseEnter={(e) => { if (!isSaving && !isGeneratingCode) e.currentTarget.style.backgroundColor = PRIMARY_DARK; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = PRIMARY; }}>
                        <Save size={18} />{isSaving ? 'Saving…' : 'Save'}
                    </button>
                    {jobId && <PrintDocuments formData={formData} masterData={masterData} companyName={companyName} />}
                    {jobId && (
                        <button onClick={handleDelete} disabled={!!formData.InvoiceNo}
                            className="flex items-center gap-2 px-4 py-2.5 text-white border-0 rounded-md text-sm font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
                            style={{ backgroundColor: '#DC2626' }}
                            onMouseEnter={(e) => { if (!formData.InvoiceNo) e.currentTarget.style.backgroundColor = '#B91C1C'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#DC2626'; }}>
                            <Trash2 size={18} /> Delete
                        </button>
                    )}
                    <button onClick={() => setShowHelp(true)} title="How to use this form"
                        className="flex items-center gap-2 px-4 py-2.5 text-white border-0 rounded-md text-sm font-medium cursor-pointer transition-colors duration-150"
                        style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.35)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)'; }}>
                        <HelpCircle size={18} />
                    </button>
                    <button onClick={onClose}
                        className="flex items-center gap-2 px-4 py-2.5 text-white border-0 rounded-md text-sm font-medium cursor-pointer transition-colors duration-150"
                        style={{ backgroundColor: '#64748B' }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#475569'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#64748B'; }}>
                        <X size={18} /> Close
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex bg-gray-50 border-b-2 border-[#E2E8F0] overflow-x-auto">
                {tabs.map(tab => (
                    <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 px-5 py-3 border-0 bg-transparent cursor-pointer text-sm font-medium whitespace-nowrap transition-all duration-150 border-b-[3px] ${activeTab===tab.id ? 'text-[#2563EB] border-[#2563EB] bg-white' : 'text-gray-500 border-transparent hover:bg-gray-100 hover:text-gray-700'}`}>
                        <tab.icon size={16} />
                        {tab.label}
                        {tab.id==='invoice' && formData.invoiceDetails?.some(r=>r.RevenueTypeID) && (
                            <span className="inline-block w-1.5 h-1.5 rounded-full ml-1 align-middle" style={{ background: PRIMARY }} />
                        )}
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            <div className="p-6 overflow-y-auto" style={{ minHeight:500, maxHeight:'calc(100vh - 300px)' }}>
                {activeTab==='basic'       && <BasicInfoTab  formData={formData} masterData={masterData} errors={errors} isGeneratingCode={isGeneratingCode} onChange={handleInputChange} />}
                {activeTab==='cargo'       && <CargoTab       data={formData.cargoDescriptions} onChange={(d)=>handleChildDataChange('cargoDescriptions',d)} />}
                {activeTab==='container'   && <ContainerTab   data={formData.containerDetails} masterData={masterData} onChange={(d)=>handleChildDataChange('containerDetails',d)} />}
                {activeTab==='invoice'     && <InvoiceTab     data={formData.invoiceDetails} masterData={masterData} jobDate={formData.JobDate} invoiceNo={formData.InvoiceNo} invoiceDate={formData.InvoiceDate} onChange={(d)=>handleChildDataChange('invoiceDetails',d)} onTaxChange={(t)=>handleInputChange('TotalTax',t)} onInvoiceNoChange={(v)=>handleInputChange('InvoiceNo',v)} onInvoiceDateChange={(v)=>handleInputChange('InvoiceDate',v)} />}
                {activeTab==='billofentry' && <BillOfEntryTab data={formData.billOfEntry} masterData={masterData} onChange={(d)=>handleChildDataChange('billOfEntry',d)} />}
                {activeTab==='transport'   && <TransportTab   formData={formData} masterData={masterData} onChange={handleInputChange} />}
                {activeTab==='documents'   && <DocumentsTab   jobId={formData.JobID} />}
                {activeTab==='audit'       && <AuditLogTab    data={formData.auditLog} onChange={(d)=>handleChildDataChange('auditLog',d)} />}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-[#E2E8F0] bg-gray-50">
                <div className="flex gap-6 text-xs text-gray-500">
                    {formData.CreatedAt && <span>Created: {new Date(formData.CreatedAt).toLocaleString()}</span>}
                    {formData.UpdatedAt && <span>Updated: {new Date(formData.UpdatedAt).toLocaleString()}</span>}
                </div>
            </div>

            {/* Help Modal */}
            {showHelp && (
                <div className="fixed inset-0 flex items-center justify-center p-5" style={{ background: 'rgba(0,0,0,0.65)', zIndex: 999999 }}
                    onClick={() => setShowHelp(false)}>
                    <div className="bg-white rounded-xl w-full max-w-[640px] max-h-[85vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-6 py-4 rounded-t-xl flex-shrink-0"
                            style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)` }}>
                            <h3 className="flex items-center gap-2 text-lg font-semibold m-0" style={{ color: '#fff' }}>
                                <HelpCircle size={18} /> How to Use This Form
                            </h3>
                            <button onClick={() => setShowHelp(false)}
                                className="w-8 h-8 flex items-center justify-center rounded-lg border-0 cursor-pointer bg-white/20 hover:bg-white/30 transition-colors duration-150"
                                style={{ color: '#fff' }}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6 space-y-4">
                            {helpSections.map(({ title, desc }, i) => (
                                <div key={i} className="flex gap-3">
                                    <div className="w-7 h-7 flex-shrink-0 rounded-full flex items-center justify-center text-xs font-bold text-white"
                                        style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)` }}>
                                        {i + 1}
                                    </div>
                                    <div>
                                        <div className="text-sm font-bold text-gray-800 mb-0.5">{title}</div>
                                        <div className="text-sm text-gray-600">{desc}</div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="flex justify-end px-6 py-4 border-t border-[#E2E8F0] flex-shrink-0">
                            <button onClick={() => setShowHelp(false)}
                                className="px-5 py-2.5 text-sm font-bold text-white border-0 rounded-lg cursor-pointer"
                                style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)` }}>
                                Got it
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default JobForm;